import type { IconName } from '@/shared/ui/Icon';
import type { AppMode } from '@/stores/session.store';

/**
 * La navigation de chaque espace, décrite une fois.
 *
 * Sur téléphone, les `tabs` forment la barre du bas : cinq destinations fixes
 * au plus (règle 08 du design system), exactement celles du mobile. Sur
 * ordinateur, la barre latérale reprend ces destinations puis ajoute ce que le
 * téléphone range derrière « Mon espace » : sur un grand écran, la place existe,
 * et une destination visible est une destination trouvée.
 *
 * Les libellés sont des CLÉS (i18n), jamais des mots.
 */
export type BadgeKey =
  | 'cart'
  | 'messages'
  | 'sellerOrders'
  | 'adminModeration'
  | 'adminDisputes'
  | 'adminFinances'
  | 'agentMissions'
  | 'notifications';

/** Les libellés de la navigation : des clés i18n, écrites une à une pour que le compilateur les vérifie. */
export type NavLabel =
  | 'common:nav.home'
  | 'common:nav.explore'
  | 'common:nav.publish'
  | 'common:nav.messages'
  | 'common:nav.cart'
  | 'common:nav.dashboard'
  | 'common:nav.products'
  | 'common:nav.orders'
  | 'common:nav.moderation'
  | 'common:nav.disputes'
  | 'common:nav.finances'
  | 'common:nav.missions'
  | 'common:nav.agenda'
  | 'web:shell.forYou'
  | 'web:shell.lives'
  | 'web:shell.communities'
  | 'web:shell.stock'
  | 'web:shell.lives_seller'
  | 'web:shell.billing'
  | 'web:shell.accounts'
  | 'web:shell.identities'
  | 'web:shell.reports'
  | 'web:shell.agents'
  | 'web:shell.sanctions'
  | 'web:shell.adminCommunities'
  | 'web:shell.sectors'
  | 'web:shell.platformSettings'
  | 'web:shell.audit'
  | 'web:shell.notifications'
  | 'web:shell.more'
  | 'account:menu.groups.myShop';

export type NavItem = {
  key: string;
  label: NavLabel;
  icon: IconName;
  iconActive: IconName;
  to: string;
  badge?: BadgeKey;
  /** Chemins qui allument aussi cette entrée (sous-pages). */
  match?: ReadonlyArray<string>;
  /** `true` : l'entrée n'est pas dans la barre du bas du téléphone. */
  desktopOnly?: boolean;
};

export type NavSection = { key: string; title?: NavItem['label']; items: ReadonlyArray<NavItem> };

const HOME: NavItem = { key: 'home', label: 'common:nav.home', icon: 'home', iconActive: 'homeFill', to: '/', match: ['/listing/'] };
const EXPLORE: NavItem = { key: 'explore', label: 'common:nav.explore', icon: 'compass', iconActive: 'compassFill', to: '/explore', match: ['/product/'] };
const FOR_YOU: NavItem = { key: 'feed', label: 'web:shell.forYou', icon: 'play', iconActive: 'play', to: '/feed', desktopOnly: true };
const LIVES: NavItem = { key: 'lives', label: 'web:shell.lives', icon: 'broadcast', iconActive: 'broadcast', to: '/lives', match: ['/live/'], desktopOnly: true };
const PUBLISH: NavItem = { key: 'publish', label: 'common:nav.publish', icon: 'plusSquare', iconActive: 'plusSquareFill', to: '/publish' };
const MESSAGES: NavItem = {
  key: 'messages',
  label: 'common:nav.messages',
  icon: 'chat',
  iconActive: 'chatFill',
  to: '/messages',
  badge: 'messages',
  match: ['/conversation/'],
};
const CART: NavItem = { key: 'cart', label: 'common:nav.cart', icon: 'bag', iconActive: 'bagFill', to: '/cart', badge: 'cart', match: ['/checkout'] };
const COMMUNITIES: NavItem = {
  key: 'communities',
  label: 'web:shell.communities',
  icon: 'users',
  iconActive: 'users',
  to: '/communities',
  match: ['/community/', '/community-post/'],
  desktopOnly: true,
};

export const NAV: Record<AppMode, ReadonlyArray<NavSection>> = {
  buyer: [
    {
      key: 'main',
      // Filtrée des entrées d'ordinateur, la liste donne la barre du mobile, dans
      // son ordre : Accueil, Explorer, Publier, Messages, Panier.
      items: [HOME, EXPLORE, FOR_YOU, LIVES, PUBLISH, MESSAGES, CART, COMMUNITIES],
    },
  ],
  seller: [
    {
      key: 'main',
      items: [
        { key: 'dashboard', label: 'common:nav.dashboard', icon: 'chart', iconActive: 'chartFill', to: '/seller' },
        { key: 'products', label: 'common:nav.products', icon: 'tag', iconActive: 'tagFill', to: '/seller/products', match: ['/product/'] },
        PUBLISH,
        { key: 'orders', label: 'common:nav.orders', icon: 'box', iconActive: 'boxFill', to: '/seller/orders', badge: 'sellerOrders', match: ['/order/'] },
        MESSAGES,
      ],
    },
    {
      key: 'shop',
      title: 'account:menu.groups.myShop',
      items: [
        { key: 'stock', label: 'web:shell.stock', icon: 'grid', iconActive: 'grid', to: '/seller/stock', desktopOnly: true },
        { key: 'lives', label: 'web:shell.lives_seller', icon: 'broadcast', iconActive: 'broadcast', to: '/lives', match: ['/seller/live/', '/live/'], desktopOnly: true },
        { key: 'finances', label: 'common:nav.finances', icon: 'wallet', iconActive: 'wallet', to: '/seller/finances', desktopOnly: true },
        { key: 'billing', label: 'web:shell.billing', icon: 'receipt', iconActive: 'receiptFill', to: '/seller/billing', desktopOnly: true },
      ],
    },
  ],
  admin: [
    {
      key: 'main',
      items: [
        { key: 'dashboard', label: 'common:nav.dashboard', icon: 'chart', iconActive: 'chartFill', to: '/admin' },
        { key: 'moderation', label: 'common:nav.moderation', icon: 'shield', iconActive: 'shieldFill', to: '/admin/moderation', badge: 'adminModeration' },
        { key: 'disputes', label: 'common:nav.disputes', icon: 'flag', iconActive: 'flagFill', to: '/admin/disputes', badge: 'adminDisputes', match: ['/dispute/'] },
        { key: 'finances', label: 'common:nav.finances', icon: 'receipt', iconActive: 'receiptFill', to: '/admin/finances', badge: 'adminFinances' },
      ],
    },
    {
      key: 'registers',
      title: 'web:shell.more',
      items: [
        { key: 'accounts', label: 'web:shell.accounts', icon: 'users', iconActive: 'users', to: '/admin/accounts', match: ['/admin/account/'], desktopOnly: true },
        { key: 'kyc', label: 'web:shell.identities', icon: 'id', iconActive: 'id', to: '/admin/kyc', desktopOnly: true },
        { key: 'reports', label: 'web:shell.reports', icon: 'alert', iconActive: 'alert', to: '/admin/reports', desktopOnly: true },
        { key: 'agents', label: 'web:shell.agents', icon: 'shield', iconActive: 'shieldFill', to: '/admin/agents', desktopOnly: true },
        { key: 'sanctions', label: 'web:shell.sanctions', icon: 'lock', iconActive: 'lock', to: '/admin/sanctions', desktopOnly: true },
        { key: 'communities', label: 'web:shell.adminCommunities', icon: 'users', iconActive: 'users', to: '/admin/communities', desktopOnly: true },
        { key: 'sectors', label: 'web:shell.sectors', icon: 'tag', iconActive: 'tagFill', to: '/admin/sectors', desktopOnly: true },
        { key: 'settings', label: 'web:shell.platformSettings', icon: 'sliders', iconActive: 'sliders', to: '/admin/settings', desktopOnly: true },
        { key: 'audit', label: 'web:shell.audit', icon: 'list', iconActive: 'list', to: '/admin/audit', desktopOnly: true },
      ],
    },
  ],
  agent: [
    {
      key: 'main',
      items: [
        { key: 'missions', label: 'common:nav.missions', icon: 'shield', iconActive: 'shieldFill', to: '/agent/missions', badge: 'agentMissions', match: ['/inspection/'] },
        { key: 'agenda', label: 'common:nav.agenda', icon: 'calendar', iconActive: 'calendarFill', to: '/agent/agenda' },
        MESSAGES,
      ],
    },
  ],
};

/** Les onglets du bas, sur téléphone : ceux du mobile, dans le même ordre. */
export function mobileTabs(mode: AppMode): ReadonlyArray<NavItem> {
  return NAV[mode].flatMap((section) => section.items).filter((item) => !item.desktopOnly);
}

/** L'entrée allumée pour une adresse : la plus longue correspondance l'emporte. */
export function activeKey(mode: AppMode, pathname: string): string | null {
  let best: { key: string; length: number } | null = null;
  for (const item of NAV[mode].flatMap((section) => section.items)) {
    const candidates = [item.to, ...(item.match ?? [])];
    for (const candidate of candidates) {
      const hit =
        candidate === '/'
          ? pathname === '/'
          : pathname === candidate || pathname.startsWith(candidate.endsWith('/') ? candidate : `${candidate}/`);
      if (hit && (!best || candidate.length > best.length)) {
        best = { key: item.key, length: candidate.length };
      }
    }
  }
  return best?.key ?? null;
}

/** L'espace pro, l'administration et l'agent portent leur actif en marine ; la place de marché, en or. */
export const isWorkspace = (mode: AppMode): boolean => mode !== 'buyer';
